import React, { useState } from 'react';
import { TrendingUp, Clock, Shield, Target, BarChart3, ArrowRight, CheckCircle2 } from 'lucide-react';
import AEDIcon from './icons/AEDIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import api from '@/services/api';

const PURPOSE_OPTIONS = [
  { value: 'end_user', label: 'End User', desc: 'Primary residence or personal use', icon: '🏠' },
  { value: 'flip', label: 'Flip (≤18 months)', desc: 'Quick turnaround investment', icon: '⚡' },
  { value: 'rental_income', label: 'Rental Income', desc: 'Generate passive income', icon: '💰' },
  { value: 'long_term_hold', label: 'Long-Term Hold (3+ years)', desc: 'Capital appreciation focus', icon: '📈' },
];

const TIMELINE_OPTIONS = [
  { value: 'immediate', label: 'Immediate', desc: 'Ready to close now' },
  { value: '3_6_months', label: '3-6 Months', desc: 'Active search' },
  { value: '6_12_months', label: '6-12 Months', desc: 'Planning ahead' },
  { value: 'flexible', label: 'Flexible', desc: 'Waiting for right opportunity' },
];

const RISK_OPTIONS = [
  { value: 'conservative', label: 'Conservative', desc: 'Low risk, stable returns', color: 'text-green-400' },
  { value: 'balanced', label: 'Balanced', desc: 'Moderate risk/reward', color: 'text-gold-400' },
  { value: 'aggressive', label: 'Aggressive', desc: 'Higher risk, higher returns', color: 'text-red-400' },
];

const FINANCE_OPTIONS = [
  { value: 'cash', label: 'Cash', desc: 'Full payment ready' },
  { value: 'mortgage', label: 'Mortgage', desc: 'Bank financing' },
  { value: 'payment_plan', label: 'Payment Plan', desc: 'Developer financing' },
  { value: 'subaa', label: 'Subaa', desc: 'Subaa financing partner' },
];

const YIELD_OPTIONS = [
  { value: 'lt_5', label: '< 5%', desc: 'Focus on appreciation' },
  { value: '5_7', label: '5-7%', desc: 'Standard market yield' },
  { value: '7_9', label: '7-9%', desc: 'Above average returns' },
  { value: '9_plus', label: '9%+', desc: 'High yield focus' },
];

const LIQUIDITY_OPTIONS = [
  { value: 'high', label: 'High Liquidity Required', desc: 'Need quick exit option' },
  { value: 'moderate', label: 'Moderate', desc: 'Standard flexibility' },
  { value: 'low', label: 'Not Important', desc: 'Long-term commitment' },
];

export const BuyerProfileIntake = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    purpose: '',
    timeline: '',
    risk_tolerance: '',
    finance_method: '',
    target_yield: '5_7',
    liquidity_preference: 'moderate'
  });

  const totalSteps = 4;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/buyer/profile', profile);
      if (onComplete) onComplete(profile);
    } catch (error) {
      console.error('Failed to save profile:', error);
    }
    setLoading(false);
  };

  const canProceed = () => {
    switch (step) {
      case 1: return profile.purpose !== '';
      case 2: return profile.timeline !== '' && profile.risk_tolerance !== '';
      case 3: return profile.finance_method !== '';
      case 4: return true;
      default: return false;
    }
  };

  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
    else handleSubmit();
  };

  const SelectOption = ({ options, value, onChange, columns = 2 }) => (
    <div className={`grid grid-cols-${columns} gap-3`}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`p-4 rounded border text-left transition-all duration-300 ${
            value === opt.value 
              ? 'border-primary bg-primary/10' 
              : 'border-white/10 hover:border-primary/50'
          }`}
          data-testid={`profile-option-${opt.value}`}
        >
          <div className="flex items-start gap-2">
            {opt.icon && <span className="text-xl">{opt.icon}</span>}
            <div>
              <div className={`font-medium ${opt.color || ''}`}>{opt.label}</div>
              <div className="text-xs text-muted-foreground mt-1">{opt.desc}</div>
            </div>
          </div>
          {value === opt.value && (
            <CheckCircle2 className="w-4 h-4 text-primary absolute top-2 right-2" />
          )}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="buyer-profile-intake">
      <div className="noise-overlay" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl" />
      
      <Card className="w-full max-w-2xl glass-card border-white/10 relative z-10">
        <CardHeader className="text-center pb-4">
          <div className="flex items-center justify-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-primary" />
            <span className="text-xs uppercase tracking-[0.2em] text-primary">Investment Profile</span>
          </div>
          <CardTitle className="font-display text-2xl">Tell Us About Your Investment Goals</CardTitle>
          <CardDescription>
            This powers your personalized Rashii Score and AI recommendations.
          </CardDescription>
          
          {/* Progress */}
          <div className="flex gap-2 justify-center mt-4">
            {Array(totalSteps).fill(0).map((_, idx) => (
              <div 
                key={idx} 
                className={`w-12 h-1 rounded ${step > idx ? 'bg-primary' : 'bg-secondary'}`} 
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">Step {step} of {totalSteps}</p>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Step 1: Purpose */}
          {step === 1 && (
            <div className="space-y-4">
              <Label className="text-sm flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" />
                What is your investment purpose?
              </Label>
              <SelectOption 
                options={PURPOSE_OPTIONS} 
                value={profile.purpose} 
                onChange={(v) => setProfile({...profile, purpose: v})}
              />
            </div>
          )}
          
          {/* Step 2: Timeline & Risk */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="space-y-4">
                <Label className="text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  What is your investment timeline?
                </Label>
                <SelectOption 
                  options={TIMELINE_OPTIONS} 
                  value={profile.timeline} 
                  onChange={(v) => setProfile({...profile, timeline: v})}
                />
              </div>
              
              <div className="space-y-4">
                <Label className="text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" />
                  What is your risk tolerance?
                </Label>
                <SelectOption 
                  options={RISK_OPTIONS} 
                  value={profile.risk_tolerance} 
                  onChange={(v) => setProfile({...profile, risk_tolerance: v})}
                  columns={3}
                />
              </div>
            </div>
          )}
          
          {/* Step 3: Finance Method */}
          {step === 3 && (
            <div className="space-y-4">
              <Label className="text-sm flex items-center gap-2">
                <AEDIcon className="w-4 h-4 text-primary" />
                How do you plan to finance?
              </Label>
              <SelectOption 
                options={FINANCE_OPTIONS} 
                value={profile.finance_method} 
                onChange={(v) => setProfile({...profile, finance_method: v})}
              />
            </div>
          )}
          
          {/* Step 4: Yield & Liquidity */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="space-y-4">
                <Label className="text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  Target yield expectation?
                </Label>
                <SelectOption 
                  options={YIELD_OPTIONS} 
                  value={profile.target_yield} 
                  onChange={(v) => setProfile({...profile, target_yield: v})}
                />
              </div>
              
              <div className="space-y-4">
                <Label className="text-sm flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  Liquidity preference?
                </Label>
                <SelectOption 
                  options={LIQUIDITY_OPTIONS} 
                  value={profile.liquidity_preference} 
                  onChange={(v) => setProfile({...profile, liquidity_preference: v})}
                  columns={3}
                />
              </div>
            </div>
          )}
          
          {/* Navigation */}
          <div className="flex gap-3 pt-4">
            {step > 1 && (
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setStep(step - 1)}
              >
                Back
              </Button>
            )}
            <Button 
              className="flex-1 bg-primary text-primary-foreground"
              onClick={nextStep}
              disabled={!canProceed() || loading}
              data-testid="profile-next-btn"
            >
              {loading ? 'Saving...' : step === totalSteps ? 'Complete Profile' : 'Continue'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          
          {onSkip && step === 1 && (
            <Button 
              variant="ghost" 
              className="w-full text-muted-foreground"
              onClick={onSkip}
            >
              Skip for now
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BuyerProfileIntake;
